import {
  Inject,
  Injectable,
} from '@angular/core';
import { AuthenticationResult, IdTokenClaims } from '@azure/msal-common';
import {
  AccountInfo,
  EndSessionRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import {
  MsalGuardConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { b2cPolicies } from '../../auth.config';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable()
export class AccountService {

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private localStorageService: LocalStorageService,
    private authService: MsalService) {}

  async login(authRequest?: RedirectRequest) {
    if (this.msalGuardConfig.authRequest) {
      const request: RedirectRequest = {
        ...this.msalGuardConfig.authRequest,
        ...authRequest,
      };

      this.authService.loginRedirect(request);
    } else {
      this.authService.loginRedirect(authRequest);
    }
  }

  getIdTokenClaims(): IdTokenClaims {
    return this.authService.instance.getActiveAccount()?.idTokenClaims;
  }

  acquireTokenSilent(onAcquireTokenSilentSuccess?: (authenticationResult: AuthenticationResult) => void): void {
    const silentRequest: SilentRequest = {
      scopes: [environment.scope],
    };
    
    this.authService.acquireTokenSilent(silentRequest)
    .subscribe({
      next(response) {
        console.log('acquireTokenSilent success: ', response);
        if(onAcquireTokenSilentSuccess) {
          onAcquireTokenSilentSuccess(response);
        }
      },
      error(error) {
        console.log('acquireTokenSilent failure: ', error);
      }
    });
  }

  acquireTokenRedirect(): void {
    const redirectRequest: RedirectRequest = {
      scopes: [environment.scope],
    };

    this.authService.acquireTokenRedirect(redirectRequest);
  }

  refreshTokens(): void {
    console.log('Triggering refresh tokens request with forceRefresh true');

    const silentRequest: SilentRequest = {
      scopes: [environment.scope],
      forceRefresh: true,
    };

    this.authService.acquireTokenSilent(silentRequest);
  }

  checkAndSetActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.accountExists()) {
      const accounts = this.authService.instance.getAllAccounts();
      this.setActiveAccount(accounts[0]);
    }
  }

  setActiveAccount(account: AccountInfo): void {
    this.authService.instance.setActiveAccount(account);
  }

  accountExists(): boolean {
    return this.authService.instance.getAllAccounts().length > 0;
  }

  async redirectToFlow(authority: string) {
    const flowRequest: RedirectRequest = {
      authority: authority,
      scopes: [],
    };

    this.login(flowRequest);
  }

  logout(postLogoutRedirectUri?: string) {
    this.acquireTokenSilent((result: AuthenticationResult) => {
      this.localStorageService.clear();

      const logoutRequest: EndSessionRequest = {
        idTokenHint: result.idToken,
        onRedirectNavigate: () => true
      };
  
      if (postLogoutRedirectUri) {
        logoutRequest.postLogoutRedirectUri = postLogoutRedirectUri;
      }
  
      this.authService.logoutRedirect(logoutRequest);
    });
  }

  remoteLogout() {
    const logoutRequest: EndSessionRequest = {
      authority: `https://login.microsoftonline.com/${environment.tenantId}`,
      onRedirectNavigate: () => false
    };
    this.authService.logoutRedirect(logoutRequest);
  }
}
